// Player customization UI
import { CHARACTER_STYLES, COLOR_PALETTES, getStyleIds, getPaletteIds } from '../config/customization.js';
import { loadPlayerCustomizations, savePlayerCustomization, getPlayerCustomization } from '../systems/storage.js';
import { playSound } from '../systems/audio.js';
import { getFirstGamepad, getGamepadForPlayer, isButtonPressed, getAxisValue } from '../config/controls.js';
import { GAMEPAD_BUTTONS, GAMEPAD_AXES } from '../config/controls.js';
import { isTouchDevice } from '../systems/touch.js';

// Key codes for player-specific controls
const PLAYER_KEYS = {
    1: {
        up: ['KeyW'],
        down: ['KeyS'],
        left: ['KeyA'],
        right: ['KeyD'],
        confirm: ['Space', 'KeyF'],
        back: ['Escape', 'ShiftLeft'],
        tab: ['KeyQ', 'KeyE']
    },
    2: {
        up: ['ArrowUp'],
        down: ['ArrowDown'],
        left: ['ArrowLeft'],
        right: ['ArrowRight'],
        confirm: ['Enter', 'End'],
        back: ['Backspace', 'ControlRight'],
        tab: ['PageUp', 'PageDown']
    }
};

// Customization state
let customizationActive = false;
let currentPlayer = 1;
let selectedStyleIndex = 0;
let selectedPaletteIndex = 0;
let activeTab = 'style'; // 'style' or 'color'
let navCooldown = 0;
let selectCooldown = 0;

// Get current selections
function getStyleIds_() { return getStyleIds(); }
function getPaletteIds_() { return getPaletteIds(); }

// Show customization screen for a player
export function showCustomization(playerNumber, playerCount, onComplete) {
    customizationActive = true;
    currentPlayer = playerNumber;
    activeTab = 'style';

    // Load current customization
    const current = getPlayerCustomization(playerNumber);
    const styleIds = getStyleIds_();
    const paletteIds = getPaletteIds_();

    selectedStyleIndex = Math.max(0, styleIds.indexOf(current.style));
    selectedPaletteIndex = Math.max(0, paletteIds.indexOf(current.palette));

    const container = document.createElement('div');
    container.id = 'customization-screen';
    container.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
        display: flex;
        flex-direction: column;
        align-items: center;
        z-index: 10000;
        font-family: 'Arial', sans-serif;
        padding: 20px;
        box-sizing: border-box;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
    `;

    document.body.appendChild(container);
    addCustomizationStyles();
    renderCustomization(container, playerNumber, playerCount, onComplete);
    startCustomizationInput(container, playerNumber, playerCount, onComplete);
}

// Add CSS styles for customization
function addCustomizationStyles() {
    if (document.getElementById('customization-styles')) return;

    const style = document.createElement('style');
    style.id = 'customization-styles';
    style.textContent = `
        .custom-title {
            font-size: 36px;
            font-weight: bold;
            color: #fff;
            text-shadow: 0 0 20px #ff6b6b;
            margin-bottom: 10px;
        }

        .custom-subtitle {
            font-size: 18px;
            color: #feca57;
            margin-bottom: 20px;
        }

        .custom-tabs {
            display: flex;
            gap: 10px;
            margin-bottom: 20px;
        }

        .custom-tab {
            padding: 12px 30px;
            font-size: 16px;
            font-weight: bold;
            color: #888;
            background: rgba(255, 255, 255, 0.05);
            border: 2px solid transparent;
            border-radius: 10px;
            cursor: pointer;
            transition: all 0.2s ease;
        }

        .custom-tab:hover {
            background: rgba(255, 255, 255, 0.1);
            color: #fff;
        }

        .custom-tab.active {
            background: linear-gradient(135deg, #ff6b6b 0%, #feca57 100%);
            color: #fff;
            border-color: #fff;
        }

        .custom-content {
            display: flex;
            gap: 30px;
            align-items: flex-start;
            max-width: 900px;
            width: 100%;
        }

        .custom-options {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
            max-width: 500px;
        }

        .custom-option {
            padding: 15px;
            background: rgba(255, 255, 255, 0.05);
            border: 3px solid transparent;
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.2s ease;
            text-align: center;
        }

        .custom-option:hover {
            background: rgba(255, 255, 255, 0.1);
            transform: scale(1.02);
        }

        .custom-option.selected {
            background: rgba(255, 107, 107, 0.2);
            border-color: #ff6b6b;
            box-shadow: 0 0 20px rgba(255, 107, 107, 0.3);
        }

        .custom-option-name {
            font-size: 14px;
            font-weight: bold;
            color: #fff;
            margin-bottom: 5px;
        }

        .custom-option-desc {
            font-size: 11px;
            color: #888;
        }

        .custom-color-swatch {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            margin: 0 auto 8px;
            border: 3px solid rgba(255, 255, 255, 0.3);
        }

        .custom-preview {
            background: rgba(0, 0, 0, 0.3);
            border-radius: 15px;
            padding: 20px;
            text-align: center;
            min-width: 220px;
        }

        .custom-preview-title {
            font-size: 14px;
            color: #888;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 2px;
        }

        .custom-preview-canvas {
            width: 200px;
            height: 200px;
            margin: 0 auto;
            position: relative;
        }

        .custom-preview-info {
            margin-top: 15px;
            font-size: 16px;
            color: #fff;
        }

        .custom-preview-style {
            color: #feca57;
            font-weight: bold;
        }

        .custom-preview-palette {
            color: #888;
            font-size: 14px;
        }

        .custom-actions {
            display: flex;
            gap: 15px;
            margin-top: 25px;
        }

        .custom-btn {
            padding: 15px 40px;
            font-size: 18px;
            font-weight: bold;
            border: none;
            border-radius: 10px;
            cursor: pointer;
            transition: all 0.2s ease;
        }

        .custom-btn-primary {
            background: linear-gradient(135deg, #ff6b6b 0%, #feca57 100%);
            color: #fff;
        }

        .custom-btn-primary:hover {
            transform: scale(1.05);
            box-shadow: 0 0 20px rgba(255, 107, 107, 0.5);
        }

        .custom-btn-secondary {
            background: rgba(255, 255, 255, 0.1);
            color: #fff;
            border: 2px solid rgba(255, 255, 255, 0.3);
        }

        .custom-btn-secondary:hover {
            background: rgba(255, 255, 255, 0.2);
        }

        .custom-controls-hint {
            position: absolute;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            color: #666;
            font-size: 13px;
            text-align: center;
        }

        .character-preview-svg {
            width: 100%;
            height: 100%;
        }

        /* Portrait / narrow screen responsive styles */
        @media (max-width: 700px), (max-height: 500px) {
            .custom-title {
                font-size: 24px;
                margin-bottom: 5px;
            }

            .custom-subtitle {
                font-size: 14px;
                margin-bottom: 10px;
            }

            .custom-tabs {
                margin-bottom: 10px;
            }

            .custom-tab {
                padding: 8px 20px;
                font-size: 14px;
            }

            .custom-content {
                flex-direction: column;
                align-items: center;
                gap: 15px;
                overflow-y: auto;
                max-height: calc(100vh - 220px);
            }

            .custom-options {
                grid-template-columns: repeat(2, 1fr);
                gap: 8px;
                max-width: 100%;
                order: 2;
            }

            .custom-option {
                padding: 10px;
            }

            .custom-option-name {
                font-size: 12px;
            }

            .custom-option-desc {
                font-size: 9px;
            }

            .custom-color-swatch {
                width: 30px;
                height: 30px;
                margin-bottom: 5px;
            }

            .custom-preview {
                order: 1;
                min-width: unset;
                width: 100%;
                max-width: 200px;
                padding: 10px;
            }

            .custom-preview-canvas {
                width: 120px;
                height: 120px;
            }

            .custom-preview-info {
                margin-top: 8px;
                font-size: 14px;
            }

            .custom-actions {
                margin-top: 10px;
                gap: 10px;
            }

            .custom-btn {
                padding: 12px 25px;
                font-size: 14px;
            }

            .custom-controls-hint {
                display: none;
            }
        }

        /* Extra small screens (phones in portrait) */
        @media (max-width: 400px) {
            .custom-options {
                grid-template-columns: repeat(2, 1fr);
                gap: 6px;
            }

            .custom-option {
                padding: 8px;
            }

            .custom-preview-canvas {
                width: 100px;
                height: 100px;
            }
        }

        /* Touch device optimizations */
        @media (hover: none) and (pointer: coarse) {
            .custom-option {
                min-height: 60px;
                -webkit-tap-highlight-color: transparent;
                touch-action: manipulation;
            }

            .custom-tab {
                min-height: 44px;
                -webkit-tap-highlight-color: transparent;
                touch-action: manipulation;
            }

            .custom-btn {
                min-height: 50px;
                -webkit-tap-highlight-color: transparent;
                touch-action: manipulation;
            }

            .custom-controls-hint {
                display: none;
            }
        }
    `;
    document.head.appendChild(style);
}

// Render the customization screen
function renderCustomization(container, playerNumber, playerCount, onComplete) {
    const styleIds = getStyleIds_();
    const paletteIds = getPaletteIds_();
    const currentStyle = CHARACTER_STYLES[styleIds[selectedStyleIndex]];
    const currentPalette = COLOR_PALETTES[paletteIds[selectedPaletteIndex]];

    container.innerHTML = `
        <div class="custom-title">Customize Player ${playerNumber}</div>
        <div class="custom-subtitle">${currentPlayer <= playerCount ? `Player ${currentPlayer} of ${playerCount}` : ''}</div>

        <div class="custom-tabs">
            <div class="custom-tab ${activeTab === 'style' ? 'active' : ''}" data-tab="style">Character</div>
            <div class="custom-tab ${activeTab === 'color' ? 'active' : ''}" data-tab="color">Color</div>
        </div>

        <div class="custom-content">
            <div class="custom-options" id="custom-options">
                ${activeTab === 'style' ? renderStyleOptions(styleIds) : renderColorOptions(paletteIds)}
            </div>

            <div class="custom-preview">
                <div class="custom-preview-title">Preview</div>
                <div class="custom-preview-canvas">
                    ${renderCharacterPreview(currentStyle, currentPalette)}
                </div>
                <div class="custom-preview-info">
                    <div class="custom-preview-style">${currentStyle.name}</div>
                    <div class="custom-preview-palette">${currentPalette.name} Colors</div>
                </div>
            </div>
        </div>

        <div class="custom-actions">
            <button class="custom-btn custom-btn-secondary" id="custom-back">Back</button>
            <button class="custom-btn custom-btn-primary" id="custom-confirm">
                ${playerNumber < playerCount ? 'Next Player' : 'Start Game'}
            </button>
        </div>

        <div class="custom-controls-hint">
            ${isTouchDevice()
                ? 'Tap to select options'
                : (playerNumber === 1
                    ? 'WASD: Navigate | Q/E: Switch Tab | Space: Confirm | Esc: Back | Gamepad 1'
                    : 'Arrow Keys: Navigate | PgUp/PgDn: Switch Tab | Enter: Confirm | Backspace: Back | Gamepad 2')}
        </div>
    `;

    // Add event listeners
    container.querySelectorAll('.custom-tab').forEach(tab => {
        tab.addEventListener('click', () => {
            activeTab = tab.dataset.tab;
            playSound('menuMove');
            renderCustomization(container, playerNumber, playerCount, onComplete);
        });
    });

    container.querySelectorAll('.custom-option').forEach((option, index) => {
        option.addEventListener('click', () => {
            if (activeTab === 'style') {
                selectedStyleIndex = index;
            } else {
                selectedPaletteIndex = index;
            }
            playSound('menuMove');
            renderCustomization(container, playerNumber, playerCount, onComplete);
        });
    });

    const backBtn = document.getElementById('custom-back');
    const confirmBtn = document.getElementById('custom-confirm');

    const handleBackClick = (e) => {
        e.preventDefault();
        playSound('menuMove');
        handleBack(container, playerNumber, playerCount, onComplete);
    };

    const handleConfirmClick = (e) => {
        e.preventDefault();
        playSound('menuSelect');
        handleConfirm(container, playerNumber, playerCount, onComplete);
    };

    backBtn.addEventListener('click', handleBackClick);
    backBtn.addEventListener('touchend', handleBackClick, { passive: false });

    confirmBtn.addEventListener('click', handleConfirmClick);
    confirmBtn.addEventListener('touchend', handleConfirmClick, { passive: false });
}

// Render style options
function renderStyleOptions(styleIds) {
    return styleIds.map((id, index) => {
        const style = CHARACTER_STYLES[id];
        return `
            <div class="custom-option ${index === selectedStyleIndex ? 'selected' : ''}" data-index="${index}">
                <div class="custom-option-name">${style.name}</div>
                <div class="custom-option-desc">${style.description}</div>
            </div>
        `;
    }).join('');
}

// Render color options
function renderColorOptions(paletteIds) {
    return paletteIds.map((id, index) => {
        const palette = COLOR_PALETTES[id];
        const rgb = `rgb(${Math.round(palette.primary.r * 255)}, ${Math.round(palette.primary.g * 255)}, ${Math.round(palette.primary.b * 255)})`;
        return `
            <div class="custom-option ${index === selectedPaletteIndex ? 'selected' : ''}" data-index="${index}">
                <div class="custom-color-swatch" style="background: ${rgb};"></div>
                <div class="custom-option-name">${palette.name}</div>
            </div>
        `;
    }).join('');
}

// Helper to convert color to CSS rgb string
function toRgb(colorObj) {
    return `rgb(${Math.round(colorObj.r * 255)}, ${Math.round(colorObj.g * 255)}, ${Math.round(colorObj.b * 255)})`;
}

// Render character preview as SVG - matches 3D rendering closely
function renderCharacterPreview(style, palette) {
    const primary = toRgb(palette.primary);
    const secondary = toRgb(palette.secondary);
    const accent = toRgb(palette.accent);
    const dark = toRgb(palette.dark);

    const cx = 100, cy = 100; // Center point
    let svg = '';

    // All characters have round sphere body
    const scaleX = style.body.scale.x;
    const scaleY = style.body.scale.y;
    const rx = 50 * scaleX;
    const ry = 45 * scaleY;

    // Main body (sphere shape)
    svg += `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="${primary}" />`;

    // Add gradient/shading for 3D effect
    svg += `
        <defs>
            <radialGradient id="bodyGrad" cx="35%" cy="35%" r="60%">
                <stop offset="0%" style="stop-color:white;stop-opacity:0.3" />
                <stop offset="100%" style="stop-color:${dark};stop-opacity:0.2" />
            </radialGradient>
        </defs>
        <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="url(#bodyGrad)" />
    `;

    const features = style.features;

    // Belly
    if (features.belly) {
        svg += `<ellipse cx="${cx}" cy="${cy + 12}" rx="${rx * 0.65}" ry="${ry * 0.55}" fill="${secondary}" />`;
    }

    // Style-specific features

    // CREATURE: Pointy ears + round tail
    if (style.id === 'creature') {
        // Pointed ears with tips
        svg += `
            <polygon points="${cx - 35},${cy - 35} ${cx - 50},${cy - 80} ${cx - 20},${cy - 40}" fill="${primary}" />
            <polygon points="${cx - 40},${cy - 55} ${cx - 48},${cy - 75} ${cx - 30},${cy - 55}" fill="${dark}" />
            <polygon points="${cx + 35},${cy - 35} ${cx + 50},${cy - 80} ${cx + 20},${cy - 40}" fill="${primary}" />
            <polygon points="${cx + 40},${cy - 55} ${cx + 48},${cy - 75} ${cx + 30},${cy - 55}" fill="${dark}" />
        `;
        // Round tail
        svg += `<ellipse cx="${cx + 55}" cy="${cy + 30}" rx="12" ry="10" fill="${primary}" />`;
        // Cheeks
        svg += `
            <ellipse cx="${cx - 38}" cy="${cy + 5}" rx="10" ry="8" fill="${accent}" opacity="0.7" />
            <ellipse cx="${cx + 38}" cy="${cy + 5}" rx="10" ry="8" fill="${accent}" opacity="0.7" />
        `;
    }

    // KNIGHT: Helmet with visor and plume
    if (style.id === 'knight') {
        // Helmet dome
        svg += `<ellipse cx="${cx}" cy="${cy - 30}" rx="42" ry="28" fill="${primary}" />`;
        // Visor slit
        svg += `<ellipse cx="${cx}" cy="${cy - 10}" rx="22" ry="5" fill="${dark}" />`;
        // Plume
        svg += `<ellipse cx="${cx}" cy="${cy - 65}" rx="8" ry="22" fill="${accent}" />`;
    }

    // DRAGON: Horns, wings, spikes, spiked tail
    if (style.id === 'dragon') {
        // Horns
        svg += `
            <polygon points="${cx - 28},${cy - 40} ${cx - 42},${cy - 85} ${cx - 18},${cy - 45}" fill="${dark}" />
            <polygon points="${cx + 28},${cy - 40} ${cx + 42},${cy - 85} ${cx + 18},${cy - 45}" fill="${dark}" />
        `;
        // Wings
        svg += `
            <ellipse cx="${cx - 68}" cy="${cy - 5}" rx="22" ry="38" fill="${accent}" opacity="0.85" />
            <ellipse cx="${cx + 68}" cy="${cy - 5}" rx="22" ry="38" fill="${accent}" opacity="0.85" />
        `;
        // Back spikes
        svg += `
            <polygon points="${cx},${cy - 55} ${cx - 7},${cy - 38} ${cx + 7},${cy - 38}" fill="${accent}" />
            <polygon points="${cx},${cy - 45} ${cx - 5},${cy - 32} ${cx + 5},${cy - 32}" fill="${accent}" />
        `;
        // Spiked tail
        svg += `
            <polygon points="${cx + 45},${cy + 25} ${cx + 82},${cy + 35} ${cx + 48},${cy + 38}" fill="${primary}" />
            <polygon points="${cx + 58},${cy + 26} ${cx + 68},${cy + 18} ${cx + 62},${cy + 30}" fill="${accent}" />
        `;
    }

    // SLIME: Droplets and shine
    if (style.id === 'slime') {
        // Droplets
        svg += `
            <ellipse cx="${cx - 42}" cy="${cy + 40}" rx="10" ry="16" fill="${primary}" opacity="0.9" />
            <ellipse cx="${cx + 38}" cy="${cy + 45}" rx="8" ry="14" fill="${primary}" opacity="0.9" />
            <ellipse cx="${cx + 5}" cy="${cy + 50}" rx="9" ry="12" fill="${primary}" opacity="0.9" />
        `;
        // Shine highlights
        svg += `
            <ellipse cx="${cx - 18}" cy="${cy - 25}" rx="14" ry="10" fill="white" opacity="0.4" />
            <ellipse cx="${cx - 10}" cy="${cy - 32}" rx="6" ry="4" fill="white" opacity="0.5" />
        `;
        // Cheeks
        svg += `
            <ellipse cx="${cx - 35}" cy="${cy + 8}" rx="10" ry="8" fill="${accent}" opacity="0.7" />
            <ellipse cx="${cx + 35}" cy="${cy + 8}" rx="10" ry="8" fill="${accent}" opacity="0.7" />
        `;
    }

    // ROBOT: Antenna, visor, panels, LED eyes
    if (style.id === 'robot') {
        // Antenna
        svg += `
            <rect x="${cx - 3}" y="${cy - 72}" width="6" height="22" fill="${dark}" />
            <circle cx="${cx}" cy="${cy - 78}" r="10" fill="${accent}" />
        `;
        // Visor bar
        svg += `<ellipse cx="${cx}" cy="${cy + 5}" rx="28" ry="7" fill="${accent}" opacity="0.7" />`;
        // Side panels
        svg += `
            <ellipse cx="${cx - 50}" cy="${cy}" rx="8" ry="18" fill="${dark}" />
            <ellipse cx="${cx + 50}" cy="${cy}" rx="8" ry="18" fill="${dark}" />
        `;
        // Chest light
        svg += `<circle cx="${cx}" cy="${cy + 22}" r="8" fill="${accent}" />`;
        // LED eyes (glowing rectangles)
        svg += `
            <ellipse cx="${cx - 18}" cy="${cy - 12}" rx="10" ry="14" fill="${accent}" />
            <ellipse cx="${cx + 18}" cy="${cy - 12}" rx="10" ry="14" fill="${accent}" />
        `;
        return `<svg class="character-preview-svg" viewBox="0 0 200 200">${svg}</svg>`;
    }

    // CAT: Cat ears, whiskers, nose, long tail
    if (style.id === 'cat') {
        // Cat ears (triangle)
        svg += `
            <polygon points="${cx - 32},${cy - 38} ${cx - 48},${cy - 78} ${cx - 18},${cy - 50}" fill="${primary}" />
            <polygon points="${cx - 35},${cy - 48} ${cx - 45},${cy - 70} ${cx - 25},${cy - 52}" fill="${accent}" />
            <polygon points="${cx + 32},${cy - 38} ${cx + 48},${cy - 78} ${cx + 18},${cy - 50}" fill="${primary}" />
            <polygon points="${cx + 35},${cy - 48} ${cx + 45},${cy - 70} ${cx + 25},${cy - 52}" fill="${accent}" />
        `;
        // Whiskers
        svg += `
            <line x1="${cx - 42}" y1="${cy + 3}" x2="${cx - 70}" y2="${cy - 2}" stroke="#333" stroke-width="2" />
            <line x1="${cx - 42}" y1="${cy + 10}" x2="${cx - 70}" y2="${cy + 12}" stroke="#333" stroke-width="2" />
            <line x1="${cx - 42}" y1="${cy + 17}" x2="${cx - 70}" y2="${cy + 22}" stroke="#333" stroke-width="2" />
            <line x1="${cx + 42}" y1="${cy + 3}" x2="${cx + 70}" y2="${cy - 2}" stroke="#333" stroke-width="2" />
            <line x1="${cx + 42}" y1="${cy + 10}" x2="${cx + 70}" y2="${cy + 12}" stroke="#333" stroke-width="2" />
            <line x1="${cx + 42}" y1="${cy + 17}" x2="${cx + 70}" y2="${cy + 22}" stroke="#333" stroke-width="2" />
        `;
        // Nose
        svg += `<ellipse cx="${cx}" cy="${cy + 5}" rx="7" ry="5" fill="${accent}" />`;
        // Long curved tail
        svg += `
            <path d="M ${cx + 45} ${cy + 28} Q ${cx + 78} ${cy + 18} ${cx + 72} ${cy - 15}" stroke="${primary}" stroke-width="12" fill="none" stroke-linecap="round" />
            <circle cx="${cx + 70}" cy="${cy - 18}" r="10" fill="${dark}" />
        `;
        // Cheeks
        svg += `
            <ellipse cx="${cx - 35}" cy="${cy + 8}" rx="10" ry="8" fill="${accent}" opacity="0.6" />
            <ellipse cx="${cx + 35}" cy="${cy + 8}" rx="10" ry="8" fill="${accent}" opacity="0.6" />
        `;
    }

    // Standard eyes for non-robot characters
    if (style.id !== 'robot') {
        // Knight has smaller eyes visible through visor
        if (style.id === 'knight') {
            svg += `
                <ellipse cx="${cx - 12}" cy="${cy - 10}" rx="6" ry="5" fill="white" />
                <ellipse cx="${cx - 11}" cy="${cy - 9}" rx="3" ry="4" fill="#111" />
                <ellipse cx="${cx + 12}" cy="${cy - 10}" rx="6" ry="5" fill="white" />
                <ellipse cx="${cx + 13}" cy="${cy - 9}" rx="3" ry="4" fill="#111" />
            `;
        } else {
            // Large cute eyes with shine
            svg += `
                <ellipse cx="${cx - 20}" cy="${cy - 8}" rx="16" ry="20" fill="white" />
                <ellipse cx="${cx + 20}" cy="${cy - 8}" rx="16" ry="20" fill="white" />
                <ellipse cx="${cx - 17}" cy="${cy - 5}" rx="9" ry="12" fill="#111" />
                <ellipse cx="${cx + 23}" cy="${cy - 5}" rx="9" ry="12" fill="#111" />
                <circle cx="${cx - 14}" cy="${cy - 12}" r="4" fill="white" />
                <circle cx="${cx + 26}" cy="${cy - 12}" r="4" fill="white" />
            `;
        }
    }

    return `<svg class="character-preview-svg" viewBox="0 0 200 200">${svg}</svg>`;
}

// Handle back button
function handleBack(container, playerNumber, playerCount, onComplete) {
    if (playerNumber > 1) {
        // Go back to previous player
        hideCustomization();
        showCustomization(playerNumber - 1, playerCount, onComplete);
    } else {
        // Go back to menu (cancel)
        hideCustomization();
        onComplete(null); // Signal cancellation
    }
}

// Handle confirm button
function handleConfirm(container, playerNumber, playerCount, onComplete) {
    const styleIds = getStyleIds_();
    const paletteIds = getPaletteIds_();

    // Save current player's customization
    const customization = {
        style: styleIds[selectedStyleIndex],
        palette: paletteIds[selectedPaletteIndex],
        name: `Player ${playerNumber}`
    };
    savePlayerCustomization(playerNumber, customization);

    if (playerNumber < playerCount) {
        // Move to next player
        hideCustomization();
        showCustomization(playerNumber + 1, playerCount, onComplete);
    } else {
        // All players customized, start game
        hideCustomization();
        onComplete(loadPlayerCustomizations());
    }
}

// Hide customization screen
export function hideCustomization() {
    const screen = document.getElementById('customization-screen');
    if (screen) {
        screen.remove();
    }
    customizationActive = false;
}

// Input handling for customization
function startCustomizationInput(container, playerNumber, playerCount, onComplete) {
    let keyboardHandler;
    let updateLoop;

    const cleanup = () => {
        if (keyboardHandler) {
            document.removeEventListener('keydown', keyboardHandler);
        }
        if (updateLoop) {
            cancelAnimationFrame(updateLoop);
        }
    };

    const navigate = (dx, dy) => {
        playSound('menuMove');

        if (activeTab === 'style') {
            const styleIds = getStyleIds_();
            const cols = 3;
            const currentRow = Math.floor(selectedStyleIndex / cols);
            const currentCol = selectedStyleIndex % cols;

            let newCol = (currentCol + dx + cols) % cols;
            let newRow = currentRow + dy;
            const maxRows = Math.ceil(styleIds.length / cols);
            newRow = (newRow + maxRows) % maxRows;

            let newIndex = newRow * cols + newCol;
            if (newIndex >= styleIds.length) {
                newIndex = styleIds.length - 1;
            }
            selectedStyleIndex = newIndex;
        } else {
            const paletteIds = getPaletteIds_();
            const cols = 3;
            const currentRow = Math.floor(selectedPaletteIndex / cols);
            const currentCol = selectedPaletteIndex % cols;

            let newCol = (currentCol + dx + cols) % cols;
            let newRow = currentRow + dy;
            const maxRows = Math.ceil(paletteIds.length / cols);
            newRow = (newRow + maxRows) % maxRows;

            let newIndex = newRow * cols + newCol;
            if (newIndex >= paletteIds.length) {
                newIndex = paletteIds.length - 1;
            }
            selectedPaletteIndex = newIndex;
        }

        renderCustomization(container, playerNumber, playerCount, onComplete);
    };

    const switchTab = () => {
        activeTab = activeTab === 'style' ? 'color' : 'style';
        playSound('menuMove');
        renderCustomization(container, playerNumber, playerCount, onComplete);
    };

    // Get the keys for the current player
    const keys = PLAYER_KEYS[playerNumber] || PLAYER_KEYS[1];

    keyboardHandler = (e) => {
        if (!customizationActive) return;

        // Check player-specific controls
        if (keys.left.includes(e.code)) {
            navigate(-1, 0);
        } else if (keys.right.includes(e.code)) {
            navigate(1, 0);
        } else if (keys.up.includes(e.code)) {
            navigate(0, -1);
        } else if (keys.down.includes(e.code)) {
            navigate(0, 1);
        } else if (keys.tab.includes(e.code) || e.code === 'Tab') {
            e.preventDefault();
            switchTab();
        } else if (keys.confirm.includes(e.code)) {
            playSound('menuSelect');
            cleanup();
            handleConfirm(container, playerNumber, playerCount, onComplete);
        } else if (keys.back.includes(e.code)) {
            playSound('menuMove');
            cleanup();
            handleBack(container, playerNumber, playerCount, onComplete);
        }
    };

    document.addEventListener('keydown', keyboardHandler);

    // Gamepad polling - use current player's gamepad
    const pollGamepad = () => {
        if (!customizationActive) return;

        navCooldown = Math.max(0, navCooldown - 16);
        selectCooldown = Math.max(0, selectCooldown - 16);

        // Try to get the specific player's gamepad first, fall back to any gamepad
        const pad = getGamepadForPlayer(playerNumber - 1) || getFirstGamepad();
        if (pad) {
            const xAxis = getAxisValue(pad, GAMEPAD_AXES.LEFT_STICK_X);
            const yAxis = getAxisValue(pad, GAMEPAD_AXES.LEFT_STICK_Y);
            const dpadLeft = isButtonPressed(pad, GAMEPAD_BUTTONS.DPAD_LEFT);
            const dpadRight = isButtonPressed(pad, GAMEPAD_BUTTONS.DPAD_RIGHT);
            const dpadUp = isButtonPressed(pad, GAMEPAD_BUTTONS.DPAD_UP);
            const dpadDown = isButtonPressed(pad, GAMEPAD_BUTTONS.DPAD_DOWN);
            const aButton = isButtonPressed(pad, GAMEPAD_BUTTONS.A);
            const bButton = isButtonPressed(pad, GAMEPAD_BUTTONS.B);
            const lButton = isButtonPressed(pad, GAMEPAD_BUTTONS.L);
            const rButton = isButtonPressed(pad, GAMEPAD_BUTTONS.R);

            if (navCooldown === 0) {
                if (xAxis < -0.5 || dpadLeft) {
                    navigate(-1, 0);
                    navCooldown = 200;
                } else if (xAxis > 0.5 || dpadRight) {
                    navigate(1, 0);
                    navCooldown = 200;
                } else if (yAxis < -0.5 || dpadUp) {
                    navigate(0, -1);
                    navCooldown = 200;
                } else if (yAxis > 0.5 || dpadDown) {
                    navigate(0, 1);
                    navCooldown = 200;
                } else if (lButton || rButton) {
                    switchTab();
                    navCooldown = 200;
                }
            }

            if (selectCooldown === 0) {
                if (aButton) {
                    playSound('menuSelect');
                    cleanup();
                    handleConfirm(container, playerNumber, playerCount, onComplete);
                    selectCooldown = 300;
                } else if (bButton) {
                    playSound('menuMove');
                    cleanup();
                    handleBack(container, playerNumber, playerCount, onComplete);
                    selectCooldown = 300;
                }
            }
        }

        updateLoop = requestAnimationFrame(pollGamepad);
    };

    updateLoop = requestAnimationFrame(pollGamepad);
}

// Check if customization is active
export function isCustomizationActive() {
    return customizationActive;
}
